<script setup lang="ts">
import { onMounted, ref } from "vue";

import { api, type ServiceAreaDocument } from "./api";

const props = defineProps<{ siteId: string }>();
const document = ref<ServiceAreaDocument | null>(null);
const canEdit = ref(false);
const loading = ref(true);
const saving = ref(false);
const uploading = ref(false);
const error = ref("");
const notice = ref("");

function addArea() {
  document.value?.includedAreas.push("");
}

async function uploadMap(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file || !document.value || !canEdit.value) return;
  uploading.value = true;
  error.value = "";
  notice.value = "";
  try {
    const response = await api.uploadServiceAreaImage(props.siteId, file);
    document.value.mapImageUrl = response.image.url;
    if (!document.value.mapAlt) document.value.mapAlt = "Map showing Jackie's cat-sitting area";
    notice.value = "Map uploaded. Save the service area to use it on the website.";
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to upload the map";
  } finally {
    uploading.value = false;
    input.value = "";
  }
}

async function save() {
  if (!document.value || !canEdit.value) return;
  saving.value = true;
  error.value = "";
  notice.value = "";
  try {
    document.value = (await api.updateServiceArea(props.siteId, document.value)).serviceArea;
    notice.value = "Service area saved.";
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to save the service area";
  } finally {
    saving.value = false;
  }
}

onMounted(async () => {
  try {
    const response = await api.serviceArea(props.siteId);
    document.value = response.serviceArea;
    canEdit.value = response.canEdit;
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to load the service area";
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <section class="area-card">
    <div class="module-heading">
      <div>
        <p class="eyebrow">Local coverage</p>
        <h2>Service area</h2>
        <p>Show customers where Jackie works without needing a live mapping subscription.</p>
      </div>
      <button type="button" :disabled="saving || !canEdit" @click="save">
        {{ saving ? "Saving…" : "Save service area" }}
      </button>
    </div>

    <p v-if="error" class="message error" role="alert">{{ error }}</p>
    <p v-if="notice" class="message success" role="status">{{ notice }}</p>
    <div v-if="loading" class="loading">Loading service area…</div>

    <template v-else-if="document">
      <div class="area-layout">
        <div class="fields">
          <label>Page heading<input v-model="document.title" :disabled="!canEdit" maxlength="160"></label>
          <label>Based in<input v-model="document.baseLocation" :disabled="!canEdit" maxlength="160" placeholder="Torquay"></label>
          <label class="wide">Introduction<textarea v-model="document.introduction" :disabled="!canEdit" rows="3" maxlength="1000" /></label>
          <label class="wide">Coverage summary<textarea v-model="document.coverageSummary" :disabled="!canEdit" rows="2" maxlength="500" /></label>
          <label class="wide">Boundary note<textarea v-model="document.boundaryNote" :disabled="!canEdit" rows="3" maxlength="1000" placeholder="The map is a guide—ask Jackie if your address is close to the edge." /></label>
        </div>

        <aside class="map-panel">
          <div class="map-preview">
            <img v-if="document.mapImageUrl" :src="document.mapImageUrl" :alt="document.mapAlt">
            <div v-else><strong>No map yet</strong><span>Upload the screenshot when it is ready.</span></div>
          </div>
          <label class="upload-button">
            {{ uploading ? "Uploading…" : "Upload map image" }}
            <input :disabled="uploading || !canEdit" type="file" accept="image/png,image/jpeg,image/webp" @change="uploadMap">
          </label>
          <label>Or image URL<input v-model="document.mapImageUrl" :disabled="!canEdit" maxlength="500"></label>
          <label>Map description<input v-model="document.mapAlt" :disabled="!canEdit" maxlength="300"></label>
        </aside>
      </div>

      <section class="places">
        <div class="section-heading">
          <div><h3>Places covered</h3><p>These appear as friendly location chips below the map.</p></div>
          <button type="button" :disabled="!canEdit" @click="addArea">Add place</button>
        </div>
        <div class="place-grid">
          <div v-for="(_area, index) in document.includedAreas" :key="index" class="place-row">
            <input v-model="document.includedAreas[index]" :disabled="!canEdit" maxlength="120" placeholder="Neighbourhood or postcode">
            <button class="remove" type="button" :disabled="!canEdit" @click="document.includedAreas.splice(index, 1)">Remove</button>
          </div>
        </div>
      </section>
    </template>
  </section>
</template>

<style scoped>
.area-card,.places{display:grid;gap:1.2rem}.area-card{padding:1.5rem;border:1px solid #d9ddd8;border-radius:1rem;background:#fff}.module-heading,.section-heading{display:flex;align-items:center;justify-content:space-between;gap:1rem}.module-heading h2,.section-heading h3{margin:0}.module-heading p:last-child,.section-heading p{margin:.35rem 0 0;color:#6d7972}.eyebrow{margin:0 0 .25rem;color:#6b7b72;font-size:.68rem;font-weight:800;letter-spacing:.1em;text-transform:uppercase}.area-layout{display:grid;grid-template-columns:minmax(0,1fr) minmax(17rem,.72fr);gap:1.2rem}.fields,.map-panel{display:grid;align-content:start;grid-template-columns:1fr 1fr;gap:.85rem}.wide,.map-preview,.upload-button{grid-column:1/-1}label{display:grid;gap:.4rem}textarea{width:100%;padding:.75rem;resize:vertical;border:1px solid #cbd5ce;border-radius:.65rem;background:#fff;font:inherit}.map-panel{padding:1rem;border:1px solid #dce2dc;border-radius:.8rem;background:#fafbf9}.map-preview{display:grid;min-height:16rem;overflow:hidden;place-items:center;border-radius:.7rem;color:#6d7972;background:#e9efeb;text-align:center}.map-preview img{width:100%;height:100%;max-height:24rem;object-fit:contain}.map-preview div{display:grid;gap:.35rem}.upload-button{display:block;padding:.75rem;border-radius:.65rem;color:#fff;background:#285c47;text-align:center;cursor:pointer}.upload-button input{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0)}.places{padding-top:1.2rem;border-top:1px solid #dce2dc}.section-heading button{padding:.55rem .7rem;color:#365045;background:#e8eeea}.place-grid{display:grid;grid-template-columns:1fr 1fr;gap:.65rem}.place-row{display:grid;grid-template-columns:1fr auto;gap:.5rem}.remove{color:#91382f!important;background:#fae9e7!important}.message{margin:0;padding:.85rem 1rem;border-radius:.65rem}.message.error{color:#86251f;background:#fee9e7}.message.success{color:#295b40;background:#e2f1e7}.loading{padding:2rem;text-align:center}@media(max-width:850px){.area-layout,.place-grid{grid-template-columns:1fr}}@media(max-width:600px){.module-heading,.section-heading{align-items:stretch;flex-direction:column}.fields{grid-template-columns:1fr}.wide{grid-column:auto}.place-row{grid-template-columns:1fr}}
</style>
